// src/components/HotspotExplanationBox.js

import React, { useMemo } from 'react';
import {
  getCategoryTitle,
  getCategorySubtitle,
  getExplanation,
  getHotspotCategory,
} from '../data/hotspotExplanations';

/**
 * Get HSL color for severity level (green 1 → yellow 3 → red 7)
 * Same algorithm as getHotspotColor in ImageAnalysisSlide
 */
function getSeverityColor(severity) {
  const value = Math.max(1, Math.min(7, severity));
  let hue;
  if (value <= 3) {
    // Green (120) to Yellow (50)
    hue = 120 - ((value - 1) / 2) * 70;
  } else {
    // Yellow (50) to Red (0)
    hue = 50 - ((value - 3) / 4) * 50;
  }
  return `hsl(${hue}, 85%, 55%)`;
}

/**
 * HOTSPOT EXPLANATION BOX
 * 
 * Displays an informational box during hotspot focus mode.
 * Positioned to be central while avoiding overlap with:
 * - The spotlight/focus ring (minimal overlap for visual connection)
 * - Top banners (88px from top)
 * - Bottom drawer
 * - Screen edges
 * 
 * Props:
 * @param {string} hotspotId - ID of the focused hotspot
 * @param {number} severity - Severity level 1-7
 * @param {object} spotlightPosition - { x, y } screen coordinates of spotlight center
 * @param {number} spotlightRadius - Radius of the spotlight in pixels
 * @param {object} bounds - { top, bottom, left, right } safe area boundaries
 * @param {number} opacity - Opacity of the box (for fade animations)
 * @param {function} onClose - Callback when close button is clicked
 */
export default function HotspotExplanationBox({
  hotspotId,
  severity = 4,
  spotlightPosition,
  spotlightRadius = 60,
  bounds = { top: 88, bottom: 200, left: 16, right: 16 },
  opacity = 1,
  onClose,
}) {
  // Get content based on hotspot category and severity
  const category = getHotspotCategory(hotspotId);
  const title = getCategoryTitle(category);
  const subtitle = getCategorySubtitle(category, severity);
  const explanation = getExplanation(category, severity);

  // Calculate optimal box position
  const boxStyle = useMemo(() => {
    const BOX_WIDTH = 300; // Fixed width for consistency
    const BOX_ESTIMATED_HEIGHT = 200; // Estimated height for positioning (actual may vary)
    const BOX_MAX_HEIGHT = 320; // Max height with scrolling
    const AESTHETIC_OVERLAP = 5; // Slight artistic overlap with spotlight (negative gap)
    const MAX_ALLOWED_OVERLAP = 40; // Maximum overlap before we consider it "too much"
    const EDGE_PADDING = 12; // Minimal padding from screen edges

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Use FULL viewport height for positioning (not constrained by drawer/banners)
    // This gives maximum flexibility to avoid spotlight overlap
    const safeTop = EDGE_PADDING;
    const safeBottom = viewportHeight - EDGE_PADDING;
    const safeLeft = bounds.left + EDGE_PADDING;
    const safeRight = viewportWidth - bounds.right - EDGE_PADDING;
    const safeWidth = safeRight - safeLeft;
    const safeHeight = safeBottom - safeTop;
    
    const actualBoxWidth = Math.min(BOX_WIDTH, safeWidth);
    const actualBoxHeight = Math.min(BOX_ESTIMATED_HEIGHT, safeHeight);

    // Default to centered if no spotlight position
    if (!spotlightPosition) {
      return {
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: `${actualBoxWidth}px`,
        maxHeight: `${BOX_MAX_HEIGHT}px`,
        opacity,
      };
    }

    const { x: spotX, y: spotY } = spotlightPosition;
    
    // Spotlight boundaries (the circle we must not significantly overlap)
    const spotTop = spotY - spotlightRadius;
    const spotBottom = spotY + spotlightRadius;
    const spotLeft = spotX - spotlightRadius;
    const spotRight = spotX + spotlightRadius;

    // Calculate available space in each direction (accounting for aesthetic overlap)
    const spaceAbove = spotTop - safeTop + AESTHETIC_OVERLAP;
    const spaceBelow = safeBottom - spotBottom + AESTHETIC_OVERLAP;
    const spaceLeft = spotLeft - safeLeft + AESTHETIC_OVERLAP;
    const spaceRight = safeRight - spotRight + AESTHETIC_OVERLAP;

    let boxX, boxY;

    // ========================================
    // HELPER: Check if box at (x,y) has TOO MUCH overlap with spotlight
    // ========================================
    const boxOverlapsSpotlightTooMuch = (bx, by, bw, bh) => {
      // Box boundaries
      const boxLeft = bx;
      const boxRight = bx + bw;
      const boxTop = by;
      const boxBottom = by + bh;
      
      // Find closest point on box to circle center
      const closestX = Math.max(boxLeft, Math.min(spotX, boxRight));
      const closestY = Math.max(boxTop, Math.min(spotY, boxBottom));
      
      // Distance from closest point to circle center
      const distX = spotX - closestX;
      const distY = spotY - closestY;
      const distance = Math.sqrt(distX * distX + distY * distY);
      
      // Return true if overlap exceeds the allowed aesthetic amount
      // (distance < radius means overlap; we allow up to MAX_ALLOWED_OVERLAP pixels of overlap)
      return distance < spotlightRadius - MAX_ALLOWED_OVERLAP;
    };

    // ========================================
    // HELPER: Calculate horizontal position to avoid excessive spotlight overlap
    // ========================================
    const getHorizontalPositionAvoidingSpotlight = (verticalY) => {
      // Try to position box so it doesn't overlap the spotlight circle too much
      // First try: place to the right of the spotlight (with aesthetic overlap)
      const rightOfSpot = spotRight - AESTHETIC_OVERLAP;
      if (rightOfSpot + actualBoxWidth <= safeRight) {
        if (!boxOverlapsSpotlightTooMuch(rightOfSpot, verticalY, actualBoxWidth, actualBoxHeight)) {
          return rightOfSpot;
        }
      }
      
      // Second try: place to the left of the spotlight (with aesthetic overlap)
      const leftOfSpot = spotLeft + AESTHETIC_OVERLAP - actualBoxWidth;
      if (leftOfSpot >= safeLeft) {
        if (!boxOverlapsSpotlightTooMuch(leftOfSpot, verticalY, actualBoxWidth, actualBoxHeight)) {
          return leftOfSpot;
        }
      }
      
      // Third try: center in safe area but offset away from spotlight
      const safeCenterX = (safeLeft + safeRight) / 2;
      const offsetDirection = spotX > safeCenterX ? -1 : 1;
      const offsetX = safeCenterX - actualBoxWidth / 2 + offsetDirection * (actualBoxWidth / 2 + 20);
      const clampedOffsetX = Math.max(safeLeft, Math.min(safeRight - actualBoxWidth, offsetX));
      if (!boxOverlapsSpotlightTooMuch(clampedOffsetX, verticalY, actualBoxWidth, actualBoxHeight)) {
        return clampedOffsetX;
      }
      
      // Fallback: just center (may have more overlap than ideal)
      return Math.max(safeLeft, Math.min(safeRight - actualBoxWidth, safeCenterX - actualBoxWidth / 2));
    };

    // ========================================
    // STRATEGY 1: Position with slight aesthetic overlap
    // ========================================
    
    // Try below spotlight (with aesthetic overlap)
    if (spaceBelow >= actualBoxHeight) {
      boxY = spotBottom - AESTHETIC_OVERLAP;
      // Position horizontally - prefer centered but shift if needed
      const centeredX = spotX - actualBoxWidth / 2;
      const clampedCenteredX = Math.max(safeLeft, Math.min(safeRight - actualBoxWidth, centeredX));
      if (!boxOverlapsSpotlightTooMuch(clampedCenteredX, boxY, actualBoxWidth, actualBoxHeight)) {
        boxX = clampedCenteredX;
      } else {
        boxX = getHorizontalPositionAvoidingSpotlight(boxY);
      }
    }
    // Try above spotlight (with aesthetic overlap)
    else if (spaceAbove >= actualBoxHeight) {
      boxY = spotTop + AESTHETIC_OVERLAP - actualBoxHeight;
      const centeredX = spotX - actualBoxWidth / 2;
      const clampedCenteredX = Math.max(safeLeft, Math.min(safeRight - actualBoxWidth, centeredX));
      if (!boxOverlapsSpotlightTooMuch(clampedCenteredX, boxY, actualBoxWidth, actualBoxHeight)) {
        boxX = clampedCenteredX;
      } else {
        boxX = getHorizontalPositionAvoidingSpotlight(boxY);
      }
    }
    // Try right of spotlight (with aesthetic overlap)
    else if (spaceRight >= actualBoxWidth) {
      boxX = spotRight - AESTHETIC_OVERLAP;
      boxY = Math.max(safeTop, Math.min(safeBottom - actualBoxHeight, spotY - actualBoxHeight / 2));
    }
    // Try left of spotlight (with aesthetic overlap)
    else if (spaceLeft >= actualBoxWidth) {
      boxX = spotLeft + AESTHETIC_OVERLAP - actualBoxWidth;
      boxY = Math.max(safeTop, Math.min(safeBottom - actualBoxHeight, spotY - actualBoxHeight / 2));
    }
    // ========================================
    // STRATEGY 2: Use partial space with horizontal shift
    // ========================================
    else {
      // Not enough clear space in any direction
      // Find the best vertical position and shift horizontally to minimize overlap
      
      const maxVerticalSpace = Math.max(spaceAbove, spaceBelow);
      
      if (maxVerticalSpace >= 60) {
        // Some vertical space available - use it (with aesthetic overlap)
        if (spaceBelow >= spaceAbove) {
          boxY = spotBottom - AESTHETIC_OVERLAP;
        } else {
          boxY = Math.max(safeTop, spotTop + AESTHETIC_OVERLAP - actualBoxHeight);
        }
        // Shift horizontally to avoid excessive spotlight overlap
        boxX = getHorizontalPositionAvoidingSpotlight(boxY);
      } else {
        // Very tight - position in opposite corner from spotlight
        if (spotY > (safeTop + safeBottom) / 2) {
          // Spotlight in lower half - put box at top
          boxY = safeTop;
        } else {
          // Spotlight in upper half - put box at bottom
          boxY = safeBottom - actualBoxHeight;
        }
        boxX = getHorizontalPositionAvoidingSpotlight(boxY);
      }
    }

    // ========================================
    // FINAL: Ensure within safe bounds
    // ========================================
    boxX = Math.max(safeLeft, Math.min(safeRight - actualBoxWidth, boxX));
    boxY = Math.max(safeTop, Math.min(safeBottom - actualBoxHeight, boxY));

    return {
      position: 'fixed',
      left: `${boxX}px`,
      top: `${boxY}px`,
      width: `${actualBoxWidth}px`,
      maxHeight: `${Math.min(BOX_MAX_HEIGHT, safeHeight)}px`,
      opacity,
    };
  }, [spotlightPosition, spotlightRadius, bounds, opacity]);

  // Don't render if opacity is 0
  if (opacity === 0) return null;

  return (
    <div
      className="z-[60] rounded-xl shadow-2xl"
      style={{
        ...boxStyle,
        backgroundColor: 'rgba(38, 38, 38, 0.95)',
        backdropFilter: 'blur(8px)',
        transition: 'opacity 300ms ease-out',
        pointerEvents: opacity > 0.5 ? 'auto' : 'none',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {/* Header */}
      <div className="relative px-4 pt-4 pb-2">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center 
                     text-gray-400 hover:text-white transition-colors rounded-full
                     hover:bg-white/10"
          aria-label="Close explanation"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 1L13 13M1 13L13 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Title */}
        <h3 className="text-white text-base font-semibold pr-8 leading-tight">
          {title}
        </h3>
        
        {/* Severity label */}
        <p 
          className="text-sm font-medium mt-0.5"
          style={{ color: getSeverityColor(severity) }}
        >
          {subtitle}
        </p>
      </div>

      {/* Explanation text */}
      <div className="px-4 pb-4">
        <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
          {explanation}
        </p>
      </div>
    </div>
  );
}
